import { GameObject } from "../core/GameObject";
import { GameScene } from "../scenes/GameScene";
import ArrayUtils from "../utils/ArrayUtils";
import { EndingDeco } from "./EndingDeco";

export class ScrollingBG {

	scene: GameScene;
	layers: BGLayer[] = [];

	constructor(scene: GameScene) {
		this.scene = scene;
		this.setBedrockLayers();
	}

	setBedrockLayers(): void {
		this.clearLayers();

		const bgTopLayer = this.addLayer(new BGLayer(this.scene, ["bedrockBGTop1", "bedrockBGTop2"], 0.95, -1000, -1224));
		this.addLayer(new BGLayer(this.scene, ["bedrockBG1", "bedrockBG2", "bedrockBG3"], 0.95, -1000, -200));
		this.addLayer(new BGLayer(this.scene, ["bedrockBGBottom1"], 0.95, -1000, 824));
		this.addLayer(new BGLayer(this.scene, ["bedrockMG1", "bedrockMG2", "bedrockMG3", "bedrockMG4"], 0.6, 3000, -150));
		const fgLayer = this.addLayer(new BGLayer(this.scene, ["bedrockFGStart", "bedrockFG1", "bedrockFG2", "bedrockFG3"], 0.5, 1800, 0));
		const fgLayer2 = this.addLayer(new BGLayer(this.scene, ["bedrockFGStartB", "bedrockFG1B", "bedrockFG2B", "bedrockFG3B"], 0.3, 3000, 0));
		this.addLayer(new SetDecoLayer(this.scene, 0));

		bgTopLayer.spawnImageFunc = this.dualSequenceSpawn(["bedrockBGTop1"]);
		fgLayer.spawnImageFunc = this.dualSequenceSpawn(["bedrockFG1", "bedrockFG2", "bedrockFG3"]);
		fgLayer2.spawnImageFunc = this.dualSequenceSpawn(["bedrockFG1B", "bedrockFG2B", "bedrockFG3B"]);

		this.update();
	}

	setMetropolisLayers(): void {
		this.clearLayers();

		const skyLayer = this.addLayer(new BGLayer(this.scene, ["metroBGTop1", "metroBGTop2"], 0.95, 0, -924));
		this.addLayer(new BGLayer(this.scene, ["metroBGMid1", "metroBGMid2", "metroBGMid3"], 0.95, 0, 100));
		this.addLayer(new BGLayer(this.scene, ["metroBGBottom"], 0.95, 0, 1124));
		this.addLayer(new BGLayer(this.scene, ["metroBuildings1", "metroBuildings2", "metroBuildings3", "metroBuildings4"], 0.8, 0, -80));
		const riverLayer = this.addLayer(new BGLayer(this.scene, ["metroRiver1", "metroRiver2"], 0.75, 0, -100));
		const trainLayer = this.addLayer(new BGLayer(this.scene, ["metroTrainTrack", "metroTrainTrack"], 0.72, 0, -115));
		this.addLayer(new BGLayer(this.scene, ["metroGrassBottom"], 0.72, 0, 250));
		this.addLayer(new BGLayer(this.scene, ["metroGrass1", "metroGrass2"], 0.72, 0, -20));
		const hazeLayer = this.addLayer(new BGLayer(this.scene, ["metroHaze"], 0, 0, 0));
		this.addLayer(new SetDecoLayer(this.scene, 1));

		skyLayer.spawnImageFunc = this.dualSequenceSpawn(["metroBGTop1"]);
		hazeLayer.spawnImageFunc = this.hazeSliverSpawn;
		riverLayer.spawnImageFunc = this.mirroredSpawn;
		
		const train = new Train(this.scene);
		train.setPosition(-99999, trainLayer.imgYOffset - 250);
		trainLayer.add(train);
		this.scene.addObject(train, "none");

		this.update();
	}

	setHauntedHouseLayers(): void {
		this.clearLayers();

		this.addLayer(new BGLayer(this.scene, ["hauntSkyTop1"], 0.95, 0, -824, "thunder-cloud", "flash", 286, -600));
		const bgMidLayer = this.addLayer(new BGLayer(this.scene, ["hauntSkyMid1", "hauntSkyMid2", "hauntSkyMid3"], 0.95, 0, 200));
		this.addLayer(new BGLayer(this.scene, ["hauntSkyBottom"], 0.95, 0, 1224));
		const backHillLayer = this.addLayer(new BGLayer(this.scene, ["hauntBackHill1", "hauntBackHill2", "hauntBackHill3", "hauntBackHill4"], 0.8, -1000, -80));
		const hillLayer = this.addLayer(new BGLayer(this.scene, ["hauntFrontHill1", "hauntFrontHill2"], 0.75, -1000, -20));
		this.addLayer(new BGLayer(this.scene, ["hauntFence1", "hauntFence2", "hauntFence3"], 0.6, -3000, 50));
		const hazeLayer = this.addLayer(new BGLayer(this.scene, ["hauntHaze"], 0, 0, 0));
		this.addLayer(new SetDecoLayer(this.scene, 1));

		bgMidLayer.spawnImageFunc = this.dualSequenceSpawn(["hauntSkyMid1", "hauntSkyMid2B", "hauntSkyMid3"]);
		backHillLayer.spawnImageFunc = this.dualSequenceSpawn(["hauntBackHill1", "hauntBackHill2"]);

		hillLayer.spawnImageFunc = this.mirroredSpawn;
		hazeLayer.spawnImageFunc = this.hazeSliverSpawn;

		this.update();
	}

	setGothamLayers(): void {
		this.clearLayers();

		const bgTopLayer = this.addLayer(new BGLayer(this.scene, ["gothamBGTop1", "gothamBGTop2"], 0.95, -1000, -824));
		const bgMidLayer = this.addLayer(new BGLayer(this.scene, ["gothamBGMid1", "gothamBGMid2", "gothamBGMid3"], 0.95, -1000, 200));
		this.addLayer(new BGLayer(this.scene, ["gothamBGBottom1", "gothamBGBottom2", "gothamBGBottom3"], 0.95, -1000, 1224));
		this.addLayer(new BGLayer(this.scene, ["gothamBuildings1", "gothamBuildings2", "gothamBuildings3", "gothamBuildings4"], 0.5, -1000, -80));
		const hazeLayer = this.addLayer(new BGLayer(this.scene, ["gothamHaze"], 0, 0, 0));
		this.addLayer(new BGLayer(this.scene, ["gothamWall"], 0, -1000, -138));
		this.addLayer(new SetDecoLayer(this.scene, 2));

		bgTopLayer.spawnImageFunc = this.dualSequenceSpawn(["gothamBGTop1"]);
		bgMidLayer.spawnImageFunc = this.dualSequenceSpawn(["gothamBGMid1", "gothamBGMid2B", "gothamBGMid3"]);
		hazeLayer.spawnImageFunc = this.hazeSliverSpawn;

		this.update();
	}

	setEndingLayers(): void {
		this.clearLayers();

		this.addLayer(new BGLayer(this.scene, ["endingSkyTop"], 0.95, 100, -1050));
		const bgMidLayer = this.addLayer(new BGLayer(this.scene, ["endingSkyBottom2", "endingSkyBottom2", "endingSkyBottom2", "endingSkyBottom1"], 0.95, -924, -26));
		const mgLayer = this.addLayer(new EmptyLayer(this.scene, 0.8, 0));
		const backlotLayer = this.addLayer(new EmptyLayer(this.scene, 0.6, 0));
		const crowdLayer = this.addLayer(new EmptyLayer(this.scene, 0.4, 0));
		const hazeLayer = this.addLayer(new BGLayer(this.scene, ["endingHaze"], 0, 0, -this.scene.ltGame.chunkSpawner.groundHeight));
		const stageLayer = this.addLayer(new EmptyLayer(this.scene, 0, 0));

		bgMidLayer.spawnImageFunc = this.dualSequenceSpawn(["endingSkyBottom2"]);
		hazeLayer.spawnImageFunc = this.hazeSliverSpawn;
		this.scene.layers.get("defaultBack").add(stageLayer); // put stage in front of ground
		stageLayer.name = "stageLayer";

		EndingDeco.create(this.scene, mgLayer, backlotLayer, crowdLayer, stageLayer);

		this.update();
	}

	// if layer doesn't cover the whole vertical screen range, mirror it and scale it up to cover the bottom with a solid color
	mirroredSpawn(this: BGLayer) {
		const img1 = this.createNextImage();
		const img2 = this.createNextImage();
		img2.scaleY = -20;
		img2.y -= 1;
		this.threshold += img1.width;
		this.imageIndex = (this.imageIndex + 1) % this.imageKeys.length;
	}

	// extend width of sliver image
	skySliverSpawn(this: BGLayer) {
		const img = this.createNextImage();
		img.setOrigin(0.5, 1);
		img.displayWidth = 2000;
		this.threshold += img.displayWidth / 2;
		this.imageIndex = (this.imageIndex + 1) % this.imageKeys.length;
	}

	hazeSliverSpawn(this: BGLayer) {
		const img = this.createNextImage();
		img.setOrigin(0, 1);
		img.displayWidth = 2000;
		this.threshold += img.displayWidth;
		this.imageIndex = (this.imageIndex + 1) % this.imageKeys.length;
	}

	// only spawns the sequence of images once, then switches to the new sequence defined in keys2
	dualSequenceSpawn(keys2: string[]) {
		return function(this: BGLayer) {
			const img = this.createNextImage();
			this.threshold += img.width;

			if (!this.getData("spawnedFirstSequence") && this.imageIndex == this.imageKeys.length - 1) {
				this.setData("spawnedFirstSequence", true);
				this.imageKeys = keys2;
				this.imageIndex = 0;
			}
			else this.imageIndex = (this.imageIndex + 1) % this.imageKeys.length;
		}
	}

	clearLayers(): void {
		for (const layer of this.layers) layer.destroy();
		this.layers = [];
	}

	addLayer(layer: BGLayer): BGLayer {
		this.layers.push(layer);
		this.scene.layers.get("bg").add(layer);
		return layer;
	}

	update(): void {
		for (const layer of this.layers) layer.update();
	}

}

export class Train extends GameObject {
	parentContainer: BGLayer;
	constructor(scene: GameScene) {
		super(scene);
		const img = this.addImage("metroTrain");
		this.setSize(img.width, img.height);
	}

	update(dt: number): void {
		super.update(dt);
		this.x -= 1000 * dt;
		if (this.x + this.width < this.parentContainer.getLeftBound().x) {
			this.x = this.parentContainer.threshold + 3000;
		}
	}
}

export class BGLayer extends GameObject {

	scene: GameScene;
	parallax: number;

	imageKeys: string[] = ["bedrockBG1", "bedrockBG2", "bedrockBG3"];
	images: Phaser.GameObjects.Image[] = [];
	imageIndex = 0;
	threshold = 0;
	xOffset = 0;
	yOffset = 0;
	imgYOffset = 0;
	spawnImageFunc: (this: BGLayer) => any;
	spineContainer: SpineContainer;
	spine: string;
	spineAnim: string;
	addSpineFlag: boolean;
	spineOffset: Phaser.Math.Vector2;

	constructor(scene: GameScene, imageKeys: string[], parallax: number, threshold = 0, yOffset = 0, spine?: string, spineAnim?: string, spineOffsetX = 0, spineOffsetY = 0) {
		super(scene);
		this.imageKeys = imageKeys;
		this.parallax = parallax;
		this.x = this.scene.cameras.main.scrollX * this.parallax;
		this.threshold = this.getLeftBound().x + threshold;
		this.imgYOffset = yOffset;
		if (spine) {
			this.spineContainer = scene.add.spineContainer(0, 0);
			this.add(this.spineContainer);
			this.spine = spine;
			this.spineAnim = spineAnim;
			this.addSpineFlag = true;
			this.spineOffset = new Phaser.Math.Vector2(spineOffsetX, spineOffsetY);
		}
		this.spawnImageFunc = () => this.spawnImage();
	}

	update(): void {
		this.x = ((this.scene.cameras.main.scrollX - this.xOffset) * this.parallax) + this.xOffset;
		this.y = ((this.scene.cameras.main.scrollY - this.yOffset) * this.parallax) + this.yOffset + this.scene.viewport.height;
		while (this.getRightBound().x > this.threshold) {
			this.spawnImageFunc();
		}

		this.destroyImagesOutOfBounds();
	}

	destroyImagesOutOfBounds() {
		const leftBound = this.getLeftBound().x;
		for (const img of [...this.images]) {
			if (img.x + img.displayWidth < leftBound - 800) {
				img.destroy();
				ArrayUtils.remove(this.images, img);
			}
		}
	}

	spawnImage() {
		const img = this.createNextImage();
		this.threshold += img.width;
		this.imageIndex = (this.imageIndex + 1) % this.imageKeys.length;
	}

	createNextImage() {
		const img = this.addImage(this.imageKeys[this.imageIndex]);
		this.images.push(img);
		img.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
		img.x = this.threshold;
		img.y = this.imgYOffset;
		img.setOrigin(0, 1);
		if (this.spine) {
			// Only add it every other tile
			if (this.addSpineFlag) {
				this.addSpineFlag = false;
				const spineObj = this.scene.add.spine(img.x + this.spineOffset.x, img.y + this.spineOffset.y, this.spine, this.spineAnim, (this.spineAnim != undefined));
				// console.log("Spawning spine " + this.spine, spineObj.x, spineObj.y);
				this.spineContainer.add(spineObj);
				this.bringToTop(this.spineContainer);
			}
			else this.addSpineFlag = true;
		}
		return img;
	}

	addImage(key: string): Phaser.GameObjects.Image {
		const img = super.addImage(key);
		this.images.push(img);
		return img;
	}

	getLeftBound(): Phaser.Math.Vector2 {
		return this.getBound(0);
	}

	getRightBound(): Phaser.Math.Vector2 {
		return this.getBound(this.scene.viewport.width);
	}

	getBound(x: number): Phaser.Math.Vector2 {
		const y = this.scene.viewport.height / 2;
		const worldPos = this.scene.cameras.main.getWorldPoint(x, y);
		const localPos = this.getLocalTransformMatrix().applyInverse(worldPos.x, worldPos.y, new Phaser.Math.Vector2()) as Phaser.Math.Vector2;
		return localPos;
	}

}

export class SetDecoLayer extends BGLayer {
	environmentIndex: number;
	constructor(scene: GameScene, environmentIndex: number) {
		super(scene, [], 0, 0, 0);
		this.environmentIndex = environmentIndex;
	}

	createNextImage() {
		const keys = ["setDeco1", "setDeco2", "setDeco3", "setDeco4"];
		if (this.environmentIndex <= 2) keys.push("setSignsEnv" + this.environmentIndex);
		const key = Phaser.Math.RND.pick(keys);
		const img = this.addImage(key);
		this.images.push(img);
		img.x = this.threshold;
		img.y = this.imgYOffset - 137;
		img.setOrigin(0, 1);
		return img;
	}

	spawnImage(): void {
		this.createNextImage();
		this.threshold += Phaser.Math.RND.realInRange(1920, 1920 * 3);
		this.imageIndex = (this.imageIndex + 1) % this.imageKeys.length;
	}
}

export class EmptyLayer extends BGLayer {
	constructor(scene: GameScene, parallax: number, yOffset = 0) {
		super(scene, [], parallax, 0, yOffset);
	}
	spawnImage() {
		this.threshold += 1920;
	}
	destroyImagesOutOfBounds(): void {
		return;
	}
}